import type { ReactNode } from 'react'
import { useUpdateCurrentUserMutation } from '@/graphql/mutations/updateCurrentUserMutation.gql.generated'
import { resolveActionError } from '@/shared/lib/action-error'
import { useNotificationCenter } from '@/shared/notifications/notification-center'

type CurrentUserProfile = {
  id: string
  workspaceId: string
  availableWorkspaces: Array<{
    id: string
    isPersonalWorkspace: boolean
    isReadOnly?: boolean | null
    name: string
    organizationId?: string | null
    organizationName?: string | null
    ownerId: string
    workspaceRole?: string | null
  }>
} | null

export type WorkspaceOption = {
  accessRole: string | null
  id: string
  label: string
  muted: boolean
  organizationId: string | null
  organizationLabel: string
  readOnly: boolean
  suffix: ReactNode | null
}

function getWorkspaceDisplayName(workspace: {
  isPersonalWorkspace: boolean
  name: string
}) {
  return workspace.name
}

export function buildWorkspaceOptions(
  currentUser: CurrentUserProfile,
): Array<WorkspaceOption> {
  return (
    currentUser?.availableWorkspaces.map((workspace) => ({
      id: workspace.id,
      label: getWorkspaceDisplayName(workspace),
      muted: workspace.id === currentUser.workspaceId,
      organizationId: workspace.organizationId ?? null,
      organizationLabel: workspace.organizationName ?? workspace.name,
      accessRole: workspace.workspaceRole ?? null,
      readOnly: workspace.isReadOnly ?? false,
      suffix:
        workspace.id === currentUser.workspaceId ? (
          <span className="text-xs text-muted-foreground">Current</span>
        ) : null,
    })) ?? []
  )
}

export function useWorkspaceChangeAction(currentUser: CurrentUserProfile) {
  const { notify } = useNotificationCenter()
  const [updateCurrentUser] = useUpdateCurrentUserMutation()

  return async (value: string, onAddWorkspace: () => void) => {
    if (value === '__add-workspace__') {
      onAddWorkspace()

      return
    }

    if (!currentUser || value === currentUser.workspaceId) {
      return
    }

    try {
      await updateCurrentUser({
        variables: {
          input: {
            workspaceId: value,
          },
        },
      })

      notify({
        title: 'Workspace updated',
        description: 'Opening document collections in your selected workspace.',
        variant: 'success',
      })
      window.location.assign('/document-collections')
    } catch (error) {
      const actionError = resolveActionError(error, 'Please try again.')

      notify({
        title: 'Workspace switch failed',
        description: actionError.message,
        variant: 'error',
      })
    }
  }
}
